/**
 * Asset Management Portal — Client Uploaded Documents page.
 * View and download assets uploaded by clients for their assessment engagements.
 * Accessible to authorized asset managers for assigned engagements.
 * Themed with KPMG Dark Green (#098E7E).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, TextField,
  InputAdornment, Grid, IconButton, Tooltip,
} from '@mui/material';
import { Search, Inventory, CloudDownload, HourglassEmpty, CheckCircle, Cancel } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import { downloadAssetDocument } from '../../../api/assetDocuments';
import RefreshButton from '../../../components/RefreshButton';
import PortalTooltip from '../../../components/PortalTooltip';

interface ClientUpload {
  id: string;
  assessment_type: string;
  asset_class: string;
  filename: string;
  description: string | null;
  status: string;
  uploaded_by: string;
  uploaded_by_name: string | null;
  has_file: boolean;
  created_at: string | null;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending: <HourglassEmpty fontSize="small" />,
  approved: <CheckCircle fontSize="small" />,
  rejected: <Cancel fontSize="small" />,
};

const STATUS_COLORS: Record<string, 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

const PORTAL_COLOR = '#098E7E';

const ClientUploads: React.FC = () => {
  const [assets, setAssets] = useState<ClientUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchClientUploads = useCallback(() => {
    setLoading(true);
    setError(null);
    authFetch('/api/sme/assets/client-uploads')
      .then((r) => {
        if (!r.ok) return r.json().then(d => { throw new Error(d.error || `Server error ${r.status}`); });
        return r.json();
      })
      .then((data) => {
        if (data.success) setAssets(data.assets);
        else setError(data.error || 'Failed to load client uploads');
      })
      .catch((e) => setError(e.message || 'Failed to load client uploads'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });
  }, []);

  useEffect(() => { fetchClientUploads(); }, [fetchClientUploads]);

  const downloadAsset = async (asset: ClientUpload) => {
    if (!asset.has_file) {
      setError('No file available for download');
      return;
    }
    try {
      await downloadAssetDocument(`/api/sme/assets/client-uploads/${asset.id}/download`, asset.filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retrieve document');
    }
  };

  const filtered = assets.filter((a) => {
    const term = searchTerm.toLowerCase();
    return (
      a.filename.toLowerCase().includes(term) ||
      a.assessment_type.toLowerCase().includes(term) ||
      (a.uploaded_by_name || '').toLowerCase().includes(term) ||
      (a.description || '').toLowerCase().includes(term)
    );
  });

  const totalCount = assets.length;
  const pendingCount = assets.filter((a) => a.status === 'pending').length;
  const approvedCount = assets.filter((a) => a.status === 'approved').length;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <PortalTooltip tooltipKey="asset_management.page.client_uploaded_documents" showIcon>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: PORTAL_COLOR }}>
              Client Uploaded Documents
            </Typography>
          </PortalTooltip>
          <Typography variant="body2" color="text.secondary">
            Review private supporting documents from clients in your assigned engagements.
            These files are not published to the client Asset Library.
          </Typography>
        </Box>
        <RefreshButton onRefresh={fetchClientUploads} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Uploads', value: totalCount, color: PORTAL_COLOR },
          { label: 'Pending Review', value: pendingCount, color: '#ed6c02' },
          { label: 'Approved', value: approvedCount, color: '#2e7d32' },
        ].map((card) => (
          <Grid item xs={12} sm={4} key={card.label}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: card.color }}>{card.value}</Typography>
              <Typography variant="body2" color="text.secondary">{card.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by file name, assessment type, or uploader..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><Search /></InputAdornment>
          ),
        }}
      />

      {filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Inventory sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {assets.length === 0 ? 'No client uploads yet.' : 'No uploads match your search.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assessment Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((asset) => (
                <TableRow key={asset.id} hover>
                  <TableCell>
                    {asset.has_file ? (
                      <Typography
                        variant="body2"
                        onClick={() => downloadAsset(asset)}
                        sx={{
                          cursor: 'pointer',
                          color: PORTAL_COLOR,
                          textDecoration: 'underline',
                          fontWeight: 500,
                          '&:hover': { opacity: 0.75 },
                        }}
                        title="Click to open file"
                      >
                        {asset.filename}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">{asset.filename}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{asset.assessment_type}</TableCell>
                  <TableCell>{asset.uploaded_by_name || asset.uploaded_by}</TableCell>
                  <TableCell>
                    <Chip
                      icon={STATUS_ICONS[asset.status] as React.ReactElement}
                      label={asset.status}
                      size="small"
                      color={STATUS_COLORS[asset.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {asset.created_at ? new Date(asset.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {asset.description || '—'}
                  </TableCell>
                  <TableCell>
                    {asset.has_file && (
                      <Tooltip title="Download">
                        <IconButton size="small" onClick={() => downloadAsset(asset)} sx={{ color: PORTAL_COLOR }}>
                          <CloudDownload fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ClientUploads;
